import { Box, HStack, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { TableHeader } from 'components/nutrition/v2/table-header';
import Picture from 'components/picture';
import { IIngredients } from 'generated/sanity-graphql';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'state/launchdarkly/flags';
import { mapImageResourceToSanityImage } from 'state/unified-menu/utils';

type ItemIngredientsProps = {
  ingredients: IIngredients[];
};

const IngredientsText = Text.withConfig({
  variant: 'copyTwo',
  fontWeight: 'bold',
  fontSize: 'sm',
});

export const Ingredients = HStack.withConfig({
  justifyContent: 'flex-start',
  marginTop: 1,
  focusable: true,
});

export const ItemIngredients = ({ ingredients }: ItemIngredientsProps) => {
  const { formatMessage } = useIntl();
  const enableFeatureItemNutritionIngredients = useFlag(
    LaunchDarklyFlag.ENABLE_FEATURE_ITEM_NUTRITION_INGREDIENTS
  );

  const orderedIngredients = ingredients?.sort(
    (a, b) => (a.ingredients?.length || 0) - (b.ingredients?.length || 0)
  );

  if (!enableFeatureItemNutritionIngredients || ingredients?.length === 0) {
    return null;
  }

  const getFormattedIngredient = (name?: string | null, ingredients?: string[] | null) => {
    const isOnlyIngredient = ingredients?.length === 1 && ingredients[0] === name;
    const hasNoIngredients = !ingredients || ingredients.length === 0;
    if (isOnlyIngredient || hasNoIngredients) {
      return (
        <>
          <Text>•</Text> {name}
        </>
      );
    }
    return (
      <>
        <Text>•</Text> {name}:{' '}
        <Text fontWeight="normal" textTransform="capitalize">
          {ingredients?.join(', ')}
        </Text>
      </>
    );
  };

  return (
    <Box>
      <TableHeader
        testID="nutrition-ingredients-header"
        title={formatMessage({ id: 'additionalInfoIngredients' })}
      />
      {orderedIngredients?.map(
        ({ ingredientImage, ingredientName, ingredients }, index: number) => {
          const normalizedIngredients = ingredients
            ? ([...ingredients].filter(x => x != null) as string[])
            : null;
          return (
            <Ingredients key={index}>
              {ingredientImage?.asset?._id && (
                <Box>
                  <Picture
                    image={mapImageResourceToSanityImage(ingredientImage?.asset?._id ?? '')}
                    alt="itemImage"
                    objectFitContain
                    width={100}
                    height={100}
                    alignSelf="center"
                  />
                </Box>
              )}
              <Box width={ingredientImage?.asset?._id ? '90%' : '100%'}>
                <IngredientsText textAlign="left" focusable>
                  {getFormattedIngredient(ingredientName, normalizedIngredients)}
                </IngredientsText>
              </Box>
            </Ingredients>
          );
        }
      )}
    </Box>
  );
};
